import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Button, Card, message, Modal, Space } from "antd";
import { LockOutlined, UserOutlined, ReloadOutlined } from "@ant-design/icons";
import { adminLogin, resetUnauthorizedFlag } from "../../api";
import { firstAllowedRoute, menuRouteVisible, setAdminAuthExtras } from "../../utils/adminPermissions";
import "../../index.css";

// 生成图形验证码
const generateCaptcha = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 120;
  canvas.height = 40;
  const ctx = canvas.getContext('2d');
  
  // 背景色
  ctx.fillStyle = '#f0f2f5';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // 生成4位随机字符
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let captcha = '';
  for (let i = 0; i < 4; i++) {
    captcha += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  // 绘制字符
  ctx.font = 'bold 24px Arial';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  
  for (let i = 0; i < captcha.length; i++) {
    const x = (canvas.width / (captcha.length + 1)) * (i + 1);
    const y = canvas.height / 2;
    const angle = (Math.random() - 0.5) * 0.4; // 随机旋转角度
    
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.fillStyle = `rgb(${Math.floor(Math.random() * 100)}, ${Math.floor(Math.random() * 100)}, ${Math.floor(Math.random() * 100)})`;
    ctx.fillText(captcha[i], 0, 0);
    ctx.restore();
  }
  
  // 绘制干扰线
  for (let i = 0; i < 5; i++) {
    ctx.strokeStyle = `rgb(${Math.floor(Math.random() * 200)}, ${Math.floor(Math.random() * 200)}, ${Math.floor(Math.random() * 200)})`;
    ctx.beginPath();
    ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
    ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
    ctx.stroke();
  }
  
  // 绘制干扰点
  for (let i = 0; i < 50; i++) {
    ctx.fillStyle = `rgb(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)})`;
    ctx.beginPath();
    ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, 1, 0, Math.PI * 2);
    ctx.fill();
  }
  
  return { image: canvas.toDataURL(), code: captcha };
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [captchaImage, setCaptchaImage] = useState('');
  const captchaCodeRef = useRef('');
  const [form] = Form.useForm();

  // 初始化验证码
  useEffect(() => {
    refreshCaptcha();
  }, []);

  const refreshCaptcha = () => {
    const { image, code } = generateCaptcha();
    setCaptchaImage(image);
    captchaCodeRef.current = code;
    // 清除验证码输入框
    form.setFieldsValue({ captcha: '' });
  };

  const handleLogin = async (values) => {
    // 验证验证码
    if (values.captcha?.toUpperCase() !== captchaCodeRef.current.toUpperCase()) {
      Modal.error({
        title: '验证码错误',
        content: '请输入正确的验证码',
        okText: '确定',
        onOk: () => {
          refreshCaptcha();
        },
      });
      return;
    }

    setLoading(true);
    try {
      // 从values中移除captcha字段，不发送到后端
      const { captcha, ...loginData } = values;
      const res = await adminLogin(loginData);
      
      // 判断登录是否成功
      if (res && res.success === true) {
        // 重置未授权标志位
        resetUnauthorizedFlag();
        localStorage.setItem("admin_token", res.token);
        localStorage.setItem("admin_username", res.user.username);
        setAdminAuthExtras(res.user);
        message.success("登录成功");
        const perms = res.user?.isSuper ? ["*"] : (res.user?.permissions || []);
        const rawFrom = location.state?.from;
        const fromPath = rawFrom ? String(rawFrom).replace(/^#/, "") : "";
        const targetPath =
          fromPath && menuRouteVisible(fromPath, perms) ? fromPath : firstAllowedRoute(perms);
        navigate(targetPath, { replace: true });
        setLoading(false);
      } else {
        // 登录失败：使用Modal弹窗显示错误信息
        setLoading(false);
        // 先清理可能存在的弹窗
        Modal.destroyAll();
        
        // 强制刷新验证码
        refreshCaptcha();
        
        // 由于 React 19 兼容性问题，使用原生 alert 作为主要提示方式
        // 这是最可靠的方式，确保用户能看到错误提示
        alert('用户名或密码不正确，请重新登陆');
        
        // 同时尝试使用 message.error（如果 React 19 兼容）
        try {
          message.error('用户名或密码不正确，请重新登陆', 4);
        } catch (msgErr) {
          console.error('message.error 失败:', msgErr);
        }
        
        // 最后尝试显示 Modal（如果 React 19 兼容）
        setTimeout(() => {
          try {
            Modal.error({
              title: '登录失败',
              content: '用户名或密码不正确，请重新登陆',
              okText: '确定',
              maskClosable: false,
              zIndex: 99999,
              onOk: () => {
                refreshCaptcha();
              },
            });
          } catch (modalErr) {
            console.error('Modal.error 失败:', modalErr);
          }
        }, 100);
      }
    } catch (err) {
      // 捕获所有错误（包括网络错误、HTTP错误等，如400、401、500等）
      console.log('登录错误:', err);
      console.log('错误响应状态:', err?.response?.status);
      console.log('错误响应数据:', err?.response?.data);
      setLoading(false);
      
      // 先清理可能存在的弹窗
      Modal.destroyAll();
      
      // 强制刷新验证码
      refreshCaptcha();
      
      // 由于 React 19 兼容性问题，使用原生 alert 作为主要提示方式
      // 这是最可靠的方式，确保用户能看到错误提示
      alert('用户名或密码不正确，请重新登陆');
      
      // 同时尝试使用 message.error（如果 React 19 兼容）
      try {
        message.error('用户名或密码不正确，请重新登陆', 4);
      } catch (msgErr) {
        console.error('message.error 失败:', msgErr);
      }
      
      // 最后尝试显示 Modal（如果 React 19 兼容）
      setTimeout(() => {
        try {
          Modal.error({
            title: '登录失败',
            content: '用户名或密码不正确，请重新登陆',
            okText: '确定',
            maskClosable: false,
            zIndex: 99999,
            onOk: () => {
              refreshCaptcha();
            },
          });
        } catch (modalErr) {
          console.error('Modal.error 失败:', modalErr);
        }
      }, 100);
    }
  };

  return (
    <div style={{ 
      display: "flex", 
      justifyContent: "center", 
      alignItems: "center", 
      minHeight: "100vh", 
      background: "transparent",
      position: 'relative',
      zIndex: 1,
    }}>
      <Card 
        title={
          <div style={{
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: '1px',
            color: '#1a1f3a',
            textAlign: 'center',
            position: 'relative',
            padding: '8px 0',
          }}>
            <span style={{
              background: 'linear-gradient(135deg, #3366FF 0%, #5c85ff 50%, #7a9fff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              管理员登录
            </span>
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '60px',
              height: '3px',
              background: 'linear-gradient(90deg, transparent 0%, #3366FF 50%, transparent 100%)',
              borderRadius: '2px',
            }} />
          </div>
        } 
        style={{ 
          width: 420, 
          boxShadow: 
            "0 12px 48px rgba(51, 102, 255, 0.12), 0 4px 16px rgba(51, 102, 255, 0.15)",
          border: '1px solid rgba(51, 102, 255, 0.15)',
          borderRadius: '20px',
          overflow: 'hidden',
          position: 'relative',
        }}
        styles={{
          header: {
            background: 'linear-gradient(135deg, rgba(51, 102, 255, 0.08) 0%, rgba(51, 102, 255, 0.05) 100%)',
            borderBottom: '2px solid rgba(51, 102, 255, 0.12)',
            padding: '20px 24px',
          },
          body: {
            padding: '32px 24px',
            background: 'rgba(255, 255, 255, 0.95)',
          },
        }}
      >
        <Form
          name="admin_login"
          form={form}
          // 去掉默认填充值，让用户手输
          onFinish={handleLogin}
          layout="vertical" // 垂直布局，更清晰
        >
          <Form.Item
            name="username"
            label="账号"
            rules={[{ required: true, message: "请输入管理员账号！" }]}
          >
            <Input
              prefix={<UserOutlined className="site-form-item-icon" style={{ color: '#3366FF' }} />}
              placeholder="请输入账号"
              disabled={loading}
              size="large"
              style={{
                borderRadius: '10px',
                border: '1px solid rgba(51, 102, 255, 0.25)',
                height: '40px',
                fontSize: '13px',
              }}
            />
          </Form.Item>
          <Form.Item
            name="password"
            label="密码"
            rules={[{ required: true, message: "请输入密码！" }]}
          >
            <Input
              prefix={<LockOutlined className="site-form-item-icon" style={{ color: '#3366FF' }} />}
              type="password"
              placeholder="请输入密码"
              disabled={loading}
              size="large"
              style={{
                borderRadius: '10px',
                border: '1px solid rgba(51, 102, 255, 0.25)',
                height: '40px',
                fontSize: '13px',
              }}
            />
          </Form.Item>
          <Form.Item
            name="captcha"
            label="验证码"
            rules={[{ required: true, message: "请输入验证码！" }]}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
              <Input
                placeholder="请输入验证码"
                disabled={loading}
                size="large"
                style={{
                  flex: 1,
                  borderRadius: '10px',
                  border: '1px solid rgba(51, 102, 255, 0.25)',
                  height: '40px',
                  fontSize: '13px',
                }}
              />
              <div
                onClick={refreshCaptcha}
                style={{
                  width: '120px',
                  height: '40px',
                  borderRadius: '10px',
                  border: '1px solid rgba(51, 102, 255, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  background: '#fff',
                  position: 'relative',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
                title="点击刷新验证码"
              >
                {captchaImage && (
                  <img
                    src={captchaImage}
                    alt="验证码"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                )}
              </div>
              <div
                style={{
                  color: '#3366FF',
                  fontSize: '16px',
                  cursor: 'pointer',
                  padding: '4px 6px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '6px',
                  border: '1px solid rgba(51, 102, 255, 0.2)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  flexShrink: 0,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  refreshCaptcha();
                }}
                title="刷新验证码"
              >
                <ReloadOutlined />
              </div>
            </div>
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              style={{ 
                width: "100%",
                height: '42px',
                fontSize: '14px',
                fontWeight: 700,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #3366FF 0%, #5c85ff 50%, #7a9fff 100%)',
                border: 'none',
                boxShadow: '0 4px 16px rgba(51, 102, 255, 0.35)',
                letterSpacing: '1px',
              }}
              loading={loading}
              size="large"
            >
              登录
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Login;